<template>
  <div class="mx-filter-wrapper">
    <div class="searchArea">
      <div class="searchForm">
        <Input
          v-if="inputItems.length > 1"
          v-model="inputValue"
          placeholder="搜索"
          class="search-input"
          style="width: 300px"
          @on-enter="doSearch"
        >
          <Select
            v-model="selectedInputKey"
            slot="prepend"
            style="width: 100px"
          >
            <Option
              v-for="item in inputItems"
              :key="item.key"
              :value="item.key"
              >{{ item.config.label }}</Option
            >
          </Select>
          <icon-font
            slot="suffix"
            name="icon-search"
            size="14"
            class="search-input-icon"
            @click="doSearch"
          />
        </Input>
        <Input
          v-if="inputItems.length === 1"
          v-model="inputValue"
          class="search-input"
          :placeholder="inputItems[0].config.placeholder || '搜索'"
          style="width: 240px"
          @on-enter="doSearch"
        >
          <icon-font
            slot="suffix"
            name="icon-search"
            size="14"
            class="search-input-icon"
            @click="doSearch"
          />
        </Input>

        <!-- 非input类型的前两个可见项 -->
        <!-- eslint-disable-next-line vue/no-v-for-template-key-on-child -->
        <template v-for="item in visibleNonInputItems">
          <t-select
            v-if="item.config.type === 'select' && item.config.multiple"
            :key="item.key + '_multiple'"
            v-model="formData[item.key]"
            multiple
            :options="item.config.options"
            :filterable="isSelectFilterable(item)"
            :style="{ width: getSelectWidth(item) }"
            :min-collapsed-num="1"
            :placeholder="item.config.label"
            clearable
            :popup-visible="
              getMultiSelectPopupVisible(
                getMultiSelectPopupKey('search', item.key)
              )
            "
            :popup-props="{
              overlayInnerStyle: {
                width: getSelectPopupWidth(item),
                minWidth: '200px',
              },
            }"
            @popup-visible-change="
              setMultiSelectPopupVisible(
                getMultiSelectPopupKey('search', item.key),
                $event
              )
            "
          >
            <div slot="panelBottomContent" class="select-panel-footer">
              <t-button
                theme="default"
                variant="outline"
                @click.stop="
                  onMultiSelectReset(
                    item.key,
                    getMultiSelectPopupKey('search', item.key)
                  )
                "
              >
                重置
              </t-button>
              <t-button
                @click.stop="
                  onMultiSelectConfirm(
                    getMultiSelectPopupKey('search', item.key)
                  )
                "
              >
                确认
              </t-button>
            </div>
          </t-select>
          <t-select
            v-else-if="item.config.type === 'select' && !item.config.multiple"
            :key="item.key + '_single'"
            v-model="formData[item.key]"
            :options="item.config.options"
            :filterable="isSelectFilterable(item)"
            :style="{ width: getSelectWidth(item) }"
            :placeholder="item.config.label"
            clearable
            @change="onDirectFilterChange"
          />
          <t-date-range-picker
            v-else-if="item.config.type === 'dateRange'"
            :key="item.key + '_dateRange'"
            v-model="formData[item.key]"
            allow-input
            clearable
            style="width: 250px"
            :placeholder="
              item.config.placeholder
                ? [item.config.placeholder, item.config.placeholder]
                : ['开始时间', '结束时间']
            "
            @change="onDirectFilterChange"
            :presets="datePresets"
          />
          <t-date-range-picker
            v-else-if="item.config.type === 'monthRange'"
            :key="item.key + '_monthRange'"
            v-model="formData[item.key]"
            mode="month"
            allow-input
            clearable
            style="width: 250px"
            :format="item.config.format || 'YYYY-MM'"
            :value-type="item.config.valueType"
            :placeholder="
              item.config.placeholder
                ? [item.config.placeholder, item.config.placeholder]
                : ['开始月份', '结束月份']
            "
            @change="onDirectFilterChange"
          />
          <t-date-picker
            v-else-if="item.config.type === 'date'"
            :key="item.key + '_date'"
            v-model="formData[item.key]"
            allow-input
            clearable
            style="width: 180px"
            :placeholder="item.config.placeholder || item.config.label"
            :enable-time-picker="item.config.enableTimePicker"
            :format="item.config.format || 'YYYY-MM-DD'"
            :value-type="item.config.valueType"
            @change="onDirectFilterChange"
          />
          <Cascader
            v-else-if="item.config.type === 'cascader'"
            :key="item.key + '_cascader'"
            :value="formData[item.key]"
            :placeholder="item.config.label"
            :data="item.config.options"
            style="width: 180px"
            clearable
            @on-change="onCascaderChange(item.key, $event)"
          />
        </template>

        <!-- 高级筛选按钮：只有存在超过3项时才显示 -->
        <Button
          v-if="hasAdvancedItems"
          :type="hasHiddenActiveFilters ? 'primary' : 'default'"
          @click="advancedFilterVisible = true"
          :ghost="hasHiddenActiveFilters"
        >
          <icon-font name="icon-shaixuan" size="15" />
          高级筛选
        </Button>
        <t-button
          v-if="hasActiveFilters"
          theme="primary"
          variant="text"
          @click="onFilterClear"
        >
          清空已选
        </t-button>
      </div>

      <div class="area">
        <slot name="actions"></slot>
        <Button @click="doSearch">
          <icon-font name="icon-shuaxin" size="15" />
          刷新
        </Button>
        <t-button
          v-if="resolvedColumns.length > 0"
          shape="square"
          variant="outline"
          @click="openColumnSetting"
        >
          <icon-font name="icon-columns" />
        </t-button>
      </div>
    </div>

    <!-- 高级筛选抽屉 -->
    <t-drawer
      :visible.sync="advancedFilterVisible"
      header="高级筛选"
      size="500px"
      sizeDraggable
      @close="advancedFilterVisible = false"
      class="advanced-filter-drawer"
    >
      <div class="advanced-filter-form">
        <!-- eslint-disable-next-line vue/no-v-for-template-key-on-child -->
        <template v-for="item in advancedFilterItems">
          <div class="filter-item" :key="item.key">
            <label class="filter-label">{{ item.config.label }}：</label>
            <t-select
              v-if="item.config.type === 'select' && item.config.multiple"
              v-model="advancedFormData[item.key]"
              multiple
              :min-collapsed-num="1"
              :placeholder="item.config.label"
              clearable
              :options="item.config.options"
              :filterable="isSelectFilterable(item)"
              :popup-visible="
                getMultiSelectPopupVisible(
                  getMultiSelectPopupKey('advanced', item.key)
                )
              "
              :popup-props="{
                overlayInnerStyle: {
                  width: getSelectPopupWidth(item),
                  minWidth: '200px',
                },
              }"
              @popup-visible-change="
                setMultiSelectPopupVisible(
                  getMultiSelectPopupKey('advanced', item.key),
                  $event
                )
              "
            >
              <div slot="panelBottomContent" class="select-panel-footer">
                <t-button
                  theme="default"
                  variant="outline"
                  @click.stop="
                    onMultiSelectReset(
                      item.key,
                      getMultiSelectPopupKey('advanced', item.key),
                      'advanced'
                    )
                  "
                >
                  重置
                </t-button>
                <t-button
                  @click.stop="
                    onMultiSelectConfirm(
                      getMultiSelectPopupKey('advanced', item.key),
                      'advanced'
                    )
                  "
                >
                  确认
                </t-button>
              </div>
            </t-select>
            <t-select
              v-else-if="item.config.type === 'select' && !item.config.multiple"
              v-model="advancedFormData[item.key]"
              :placeholder="item.config.label"
              clearable
              :options="item.config.options"
              :filterable="isSelectFilterable(item)"
            />
            <t-date-range-picker
              v-else-if="item.config.type === 'dateRange'"
              v-model="advancedFormData[item.key]"
              allow-input
              clearable
              :placeholder="['开始时间', '结束时间']"
              style="width: 100%"
            />
            <t-date-range-picker
              v-else-if="item.config.type === 'monthRange'"
              v-model="advancedFormData[item.key]"
              mode="month"
              allow-input
              clearable
              :format="item.config.format || 'YYYY-MM'"
              :value-type="item.config.valueType"
              :placeholder="['开始月份', '结束月份']"
              style="width: 100%"
            />
            <t-date-picker
              v-else-if="item.config.type === 'date'"
              v-model="advancedFormData[item.key]"
              allow-input
              clearable
              :placeholder="item.config.placeholder || item.config.label"
              :enable-time-picker="item.config.enableTimePicker"
              :format="item.config.format || 'YYYY-MM-DD'"
              :value-type="item.config.valueType"
              style="width: 100%"
            />
            <Cascader
              :value="advancedFormData[item.key]"
              v-else-if="item.config.type === 'cascader'"
              :placeholder="item.config.label"
              :data="item.config.options"
              @on-change="onAdvancedCascaderChange(item.key, $event)"
            />
          </div>
        </template>
      </div>
      <div slot="footer" class="advanced-filter-footer">
        <t-button theme="primary" variant="text" @click="onAdvancedFilterClear"
          >清空已选</t-button
        >
        <t-button
          theme="default"
          variant="outline"
          @click="advancedFilterVisible = false"
          >取消</t-button
        >
        <t-button theme="primary" @click="onFilterConfirm">确认</t-button>
      </div>
    </t-drawer>

    <!-- 自定义列表抽屉 -->
    <t-drawer
      :visible.sync="columnSettingVisible"
      header="自定义列表"
      size="580px"
      sizeDraggable
      @close="columnSettingVisible = false"
      class="column-setting-drawer"
    >
      <p class="column-setting-info">
        清除浏览器缓存或更换浏览器，自定义设置失效，还原系统默认配置。
      </p>
      <div class="column-setting-set">
        <CheckboxGroup
          v-model="columnKeys"
          class="column-checkbox"
          v-if="columnSettingVisible"
        >
          <draggable
            v-model="columnsData"
            animation="300"
            filter=".undraggable"
            :move="onColumnMove"
          >
            <transition-group>
              <!-- eslint-disable-next-line vue/no-v-for-template-key-on-child -->
              <template v-for="item in columnsData">
                <Checkbox
                  v-if="item.title"
                  :label="item.title"
                  :key="item.title"
                  :disabled="item.fixed ? true : false"
                  :class="{ undraggable: item.fixed }"
                >
                  <span class="column-title">{{ item.title }}</span>
                  <img src="@/assets/img/checkicon.png" />
                </Checkbox>
              </template>
            </transition-group>
          </draggable>
        </CheckboxGroup>
      </div>
      <div slot="footer" class="column-setting-footer">
        <Button
          type="primary"
          style="margin-right: 8px"
          @click="saveColumnSetting"
          :loading="savingConfig"
          >保存配置</Button
        >
        <Button @click="columnSettingVisible = false">取消</Button>
      </div>
    </t-drawer>
  </div>
</template>

<script>
import draggable from 'vuedraggable'

const SEARCHABLE_COMPANY_SUPPLIER_KEYS = new Set([
  'companyId',
  'companyIds',
  'companyName',
  'decorationArray',
  'supplierId',
  'supplierIds',
  'supplierName',
])

export default {
  name: 'MXFilter',
  components: { draggable },
  props: {
    model: {
      type: Object,
      default: () => ({}),
    },
    columns: {
      type: Array,
      default: () => [],
    },
    valid: Boolean,
    visibleNonInputCount: {
      type: Number,
      default: null,
    },
    advancedItemKeys: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      // 输入框相关
      inputValue: '',
      selectedInputKey: '',
      // 表单数据（非input类型）
      formData: {},
      advancedFormData: {},
      advancedApplied: false,
      // 高级筛选抽屉
      advancedFilterVisible: false,
      // 自定义列表抽屉
      columnSettingVisible: false,
      savingConfig: false,
      columnKeys: [],
      columnsData: [],
      multiSelectPopupVisible: {},
      // 日期预设
      datePresets: {
        最近7天: [new Date(+new Date() - 86400000 * 6), new Date()],
        最近3天: [new Date(+new Date() - 86400000 * 2), new Date()],
        今天: [new Date(), new Date()],
      },
    }
  },
  computed: {
    resolvedColumns() {
      if (this.columns && this.columns.length) return this.columns
      const parentTableData = this.$parent && this.$parent.tableData
      return parentTableData && Array.isArray(parentTableData.columns)
        ? parentTableData.columns
        : []
    },
    // 所有筛选项（扁平化为数组，保留key）
    allFilterItems() {
      const items = []
      if (!this.model) return items
      Object.keys(this.model).forEach((key) => {
        const config = this.model[key]
        items.push({ key, config })
      })
      return items
    },
    // input 类型的项
    inputItems() {
      return this.allFilterItems.filter((item) => item.config.type === 'input')
    },
    // 非input类型的项（按顺序）
    nonInputItems() {
      return this.allFilterItems.filter((item) => item.config.type !== 'input')
    },
    advancedFilterItems() {
      if (!this.advancedItemKeys || this.advancedItemKeys.length === 0) {
        return this.nonInputItems
      }
      const itemMap = new Map()
      this.nonInputItems.forEach((item) => {
        itemMap.set(item.key, item)
      })
      const sortedItems = this.advancedItemKeys
        .map((key) => itemMap.get(key))
        .filter(Boolean)
      const appendedItems = this.nonInputItems.filter(
        (item) => !this.advancedItemKeys.includes(item.key)
      )
      return sortedItems.concat(appendedItems)
    },
    // 搜索栏展示的非input项（按顺序取前2或3项）
    visibleNonInputItems() {
      const maxVisible =
        this.visibleNonInputCount || (this.inputItems.length > 0 ? 2 : 3)
      return this.nonInputItems.slice(0, maxVisible)
    },
    // 搜索栏已展示的item key集合
    visibleKeys() {
      const keys = new Set()
      // input类型全部算作已展示
      this.inputItems.forEach((item) => keys.add(item.key))
      this.visibleNonInputItems.forEach((item) => keys.add(item.key))
      return keys
    },
    // 只有存在未直接展示的条件，且高级筛选抽屉有内容时才显示按钮
    hasAdvancedItems() {
      return (
        this.advancedFilterItems.length > 0 &&
        this.allFilterItems.length >
        (this.inputItems.length > 0 ? 1 : 0) + this.visibleNonInputItems.length
      )
    },
    // 是否有生效的查询条件
    hasActiveFilters() {
      const data = this.collectFormData()
      return Object.keys(data).length > 0
    },
    // 是否有在“高级筛选”中隐藏的项被设置了值
    hasHiddenActiveFilters() {
      const data = this.collectFormData()
      const hasHiddenActive = Object.keys(data).some(
        (key) => !this.visibleKeys.has(key)
      )
      return (
        hasHiddenActive ||
        (this.advancedApplied && this.hasNonInputActiveFilters(data))
      )
    },
  },
  watch: {
    model: {
      handler(newModel) {
        if (newModel) {
          this.initFormData()
        }
      },
      deep: true,
      immediate: true,
    },
    // 当搜索栏输入值变化时，同步到formData
    inputValue(val) {
      if (this.inputItems.length > 1) {
        this.$set(this.formData, this.selectedInputKey, val)
      } else if (this.inputItems.length === 1) {
        this.$set(this.formData, this.inputItems[0].key, val)
      }
    },
    // 当选中的input key变化时，同步inputValue
    selectedInputKey(newKey, oldKey) {
      // 清空旧key的值
      if (oldKey) {
        this.$set(this.formData, oldKey, '')
      }
      // 将formData中新key的值同步到inputValue
      this.inputValue = this.formData[newKey] || ''
    },
    // 打开高级筛选时，同步搜索栏数据到抽屉
    advancedFilterVisible(visible) {
      if (visible) {
        // 同步inputValue到formData
        if (this.inputItems.length > 1) {
          this.$set(this.formData, this.selectedInputKey, this.inputValue)
        } else if (this.inputItems.length === 1) {
          this.$set(this.formData, this.inputItems[0].key, this.inputValue)
        }
        this.advancedFormData = this.cloneFilterData(this.formData)
      }
    },
  },
  created() {
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    // 初始化表单数据
    initFormData() {
      if (!this.model) return
      const data = {}
      Object.keys(this.model).forEach((key) => {
        const config = this.model[key]
        if (config.type === 'input') {
          // input 类型由 inputValue 管理，但也需要在 formData 中初始化以便高级筛选使用
          data[key] = config.value || ''
        } else if (config.type === 'select' && config.multiple) {
          data[key] = Array.isArray(config.value) ? [...config.value] : []
        } else if (
          config.type === 'dateRange' ||
          config.type === 'monthRange'
        ) {
          data[key] = Array.isArray(config.value) ? [...config.value] : []
        } else {
          data[key] = config.value !== undefined ? config.value : ''
        }
      })
      this.formData = data
      this.advancedFormData = this.cloneFilterData(data)

      // 设置默认选中的input key
      if (this.inputItems.length > 0 && !this.selectedInputKey) {
        this.selectedInputKey = this.inputItems[0].key
      }

      // 如果有input项且model中有预设值，同步到inputValue
      if (this.inputItems.length > 0) {
        const selectedInput = this.inputItems.find(
          (item) => item.key === this.selectedInputKey
        )
        if (selectedInput && selectedInput.config.value) {
          this.inputValue = selectedInput.config.value
        }
      }
    },

    // 收集所有表单数据
    collectFormData() {
      const result = {}
      Object.keys(this.model).forEach((key) => {
        const config = this.model[key]
        if (config.type === 'input') {
          // 对于input类型，只有当前选中的input key才赋值
          if (this.inputItems.length > 1) {
            if (key === this.selectedInputKey && this.inputValue) {
              result[key] = this.inputValue
            }
          } else if (this.inputItems.length === 1) {
            if (this.inputValue) {
              result[key] = this.inputValue
            }
          }
        } else {
          const val = this.formData[key]
          if (val !== undefined && val !== '' && val !== null) {
            if (Array.isArray(val)) {
              // 过滤掉空字符串和null
              const cleanVal = val.filter(
                (v) => v !== undefined && v !== '' && v !== null
              )
              if (cleanVal.length > 0) {
                result[key] = val
              }
            } else if (typeof val === 'string' && val.trim() === '') {
              // 全空格字符串不传
            } else {
              result[key] = val
            }
          }
        }
      })
      return result
    },

    hasNonInputActiveFilters(data) {
      return Object.keys(data).some((key) => {
        const config = this.model[key]
        return config && config.type !== 'input'
      })
    },

    // 统一搜索触发
    doSearch() {
      const formData = this.collectFormData()
      this.$emit('on-filter', formData)
    },

    onDirectFilterChange() {
      this.advancedApplied = false
      this.doSearch()
    },
    onCascaderChange(key, value) {
      const config = this.model[key]
      const nextValue =
        config && config.emitPath
          ? value
          : value && value.length
          ? value[value.length - 1]
          : ''
      this.$set(this.formData, key, nextValue)
      this.onDirectFilterChange()
    },
    onAdvancedCascaderChange(key, value) {
      const config = this.model[key]
      const nextValue =
        config && config.emitPath
          ? value
          : value && value.length
          ? value[value.length - 1]
          : ''
      this.$set(this.advancedFormData, key, nextValue)
    },

    // 获取数据（兼容旧API）
    getData() {
      return this.collectFormData()
    },

    // 回车搜索
    handleKeydown(event) {
      if (event.key === 'Enter') {
        this.doSearch()
      }
    },

    // 多选下拉重置
    onMultiSelectReset(key, popupKey, scope = 'search') {
      const target =
        scope === 'advanced' ? this.advancedFormData : this.formData
      this.$set(target, key, [])
      this.closeMultiSelectPopup(popupKey)
      if (scope !== 'advanced') {
        this.onDirectFilterChange()
      }
    },
    onMultiSelectConfirm(popupKey, scope = 'search') {
      this.closeMultiSelectPopup(popupKey)
      if (scope !== 'advanced') {
        this.onDirectFilterChange()
      }
    },
    getMultiSelectPopupKey(type, key) {
      return `${type}-${key}`
    },
    getMultiSelectPopupVisible(key) {
      return !!this.multiSelectPopupVisible[key]
    },
    setMultiSelectPopupVisible(key, visible) {
      this.$set(this.multiSelectPopupVisible, key, visible)
    },
    closeMultiSelectPopup(key) {
      this.setMultiSelectPopupVisible(key, false)
    },

    // 下拉框宽度计算
    getSelectWidth(item) {
      if (item.config.width) return item.config.width
      return 'auto'
    },
    isSelectFilterable(item) {
      return (
        item.config.filterable === true ||
        SEARCHABLE_COMPANY_SUPPLIER_KEYS.has(item.key)
      )
    },
    getSelectPopupWidth() {
      return 'auto'
    },

    cloneFilterData(data) {
      const result = {}
      Object.keys(data || {}).forEach((key) => {
        const value = data[key]
        result[key] = Array.isArray(value) ? [...value] : value
      })
      return result
    },

    // ---- 高级筛选 ----
    onFilterClear() {
      Object.keys(this.formData).forEach((key) => {
        const config = this.model[key]
        if (!config) return
        if (config.type === 'input') {
          this.$set(this.formData, key, '')
          // 同步清空搜索栏inputValue
          if (this.inputItems.length === 1 || key === this.selectedInputKey) {
            this.inputValue = ''
          }
        } else if (config.type === 'select' && config.multiple) {
          this.$set(this.formData, key, [])
        } else if (
          config.type === 'dateRange' ||
          config.type === 'monthRange' ||
          config.type === 'cascader'
        ) {
          this.$set(this.formData, key, [])
        } else {
          this.$set(this.formData, key, '')
        }
      })
      this.advancedFormData = this.cloneFilterData(this.formData)
      this.advancedApplied = false
      this.doSearch()
    },
    onAdvancedFilterClear() {
      Object.keys(this.advancedFormData).forEach((key) => {
        const config = this.model[key]
        if (!config) return
        if (config.type === 'select' && config.multiple) {
          this.$set(this.advancedFormData, key, [])
        } else if (
          config.type === 'dateRange' ||
          config.type === 'monthRange' ||
          config.type === 'cascader'
        ) {
          this.$set(this.advancedFormData, key, [])
        } else {
          this.$set(this.advancedFormData, key, '')
        }
      })
    },
    onFilterConfirm() {
      this.formData = this.cloneFilterData(this.advancedFormData)
      this.advancedFilterVisible = false

      // 标记是否通过高级筛选应用了过滤条件（仅针对非输入项）
      const data = this.collectFormData()
      this.advancedApplied = this.hasNonInputActiveFilters(data)

      this.doSearch()
    },

    // ---- 自定义列表 ----
    openColumnSetting() {
      if (!this.resolvedColumns || this.resolvedColumns.length === 0) {
        this.$eventBus.emit('mx-open-column-setting', {
          path: this.$route.path,
        })
        return
      }
      const columnsList = Array.from(this.resolvedColumns)
      let path = this.$route.path
      let localStorageColumn = localStorage.getItem('columnJson')
      var data = null
      if (localStorageColumn) {
        try {
          data = JSON.parse(localStorageColumn)
        } catch (error) {
          data = null
        }
      }
      if (data && data[path] && data[path].length > 0) {
        var keys = []
        data[path].forEach((x) => {
          if (x.check) {
            keys.push(x.title)
          }
        })
        this.columnKeys = keys
        const list = data[path].map((item) => {
          var obj = {}
          columnsList.forEach((x) => {
            if (x.title == item.title) {
              obj = x
            }
          })
          return { ...obj, check: item.check }
        })
        this.columnsData = list
      } else {
        this.columnKeys = []
        columnsList.forEach((x) => {
          this.columnKeys.push(x.title)
        })
        this.columnsData = columnsList
      }
      this.columnSettingVisible = true
    },
    onColumnMove(event) {
      const draggedElement = event.dragged
      const targetElement = event.related
      if (
        targetElement.classList.contains('undraggable') ||
        draggedElement.classList.contains('undraggable')
      ) {
        return false
      }
    },
    saveColumnSetting() {
      this.savingConfig = true
      setTimeout(() => {
        let path = this.$route.path
        let localStorageColumn = localStorage.getItem('columnJson')
        let data = {}
        if (localStorageColumn) {
          try {
            data = JSON.parse(localStorageColumn)
          } catch (error) {
            data = {}
          }
        }
        var list = []
        const columnsList = Array.from(this.columnsData)
        columnsList.map((item) => {
          if (item.fixed && this.columnKeys.length != 0) {
            item.check = true
            list.push(item)
          } else if (this.columnKeys.find((x) => x == item.title)) {
            list.push(item)
            item.check = true
          } else {
            item.check = false
          }
          return item
        })
        data[path] = columnsList
        localStorage.setItem('columnJson', JSON.stringify(data))
        const finalColumns = list.length == 0 ? columnsList : list
        this.$emit('on-columns-change', finalColumns)
        this.$eventBus.emit('mx-column-setting-saved', {
          path: this.$route.path,
        })
        this.savingConfig = false
        this.columnSettingVisible = false
      }, 50)
    },
  },
}
</script>

<style lang="less" scoped>
.mx-filter-wrapper {
  /deep/ .ivu-input::placeholder,
  /deep/ .t-input__inner::placeholder {
    color: #86909c;
    opacity: 1;
  }

  /deep/ .ivu-select-placeholder {
    color: #86909c;
  }

  /deep/ .search-input-icon {
    display: block;
    color: #86909c;
    cursor: pointer;

    &:hover {
      color: #1456f0;
    }
  }

  /deep/ .search-input .ivu-input-suffix {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    &::before {
      content: '';
      width: 1px;
      height: 12px;
      position: absolute;
      left: -1px;
      background: #e5e6eb;
    }
  }

  .searchArea {
    height: 48px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px;
  }

  .searchForm {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    flex: 1;

    > .t-input,
    > .t-date-range-picker,
    > .t-date-picker,
    > .ivu-input-wrapper {
      flex: 0 1 auto;
      min-width: 160px;
    }

    > .t-select {
      flex: 0 1 auto;
      min-width: 100px;
    }
  }

  .area {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 10px;
    margin-left: auto;
  }

  /deep/ .ivu-input-group-prepend {
    background: #fff;
  }
  /deep/ .ivu-input-group .ivu-input {
    z-index: unset;
  }
}

// 按钮图标hover
.ivu-btn:hover .icon-font {
  color: #1456f0 !important;
}

// 多选下拉底部
.select-panel-footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
  border-top: 1px solid #e0e0e6;
  background: #fff;
  padding: 8px 12px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;

  .t-button {
    width: 44px;
    height: 26px;
    font-size: 12px;
  }
}

// 高级筛选抽屉
.advanced-filter-form {
  padding: 0 4px;

  .filter-item {
    margin-bottom: 20px;
  }

  .filter-label {
    display: block;
    font-size: 14px;
    color: #171a1d;
    margin-bottom: 8px;
  }

  /deep/ .t-select,
  /deep/ .t-date-range-picker {
    width: 100%;
  }
}

.advanced-filter-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  height: 100%;
}

/deep/ .advanced-filter-drawer {
  .ivu-input::placeholder,
  .t-input__inner::placeholder {
    color: #86909c;
    opacity: 1;
  }

  .ivu-select-placeholder {
    color: #86909c;
  }

  .t-drawer__content-wrapper {
    min-width: 300px;
    max-width: 1200px;
  }
}

// 自定义列表抽屉
.column-setting-info {
  color: #87898b;
  margin-bottom: 12px;
}

.column-setting-set {
  overflow: hidden;
  overflow-y: auto;
  height: calc(100% - 60px);
}

.column-setting-footer {
  display: flex;
  gap: 8px;
  height: 100%;
  align-items: center;
}

.column-checkbox {
  /deep/.ivu-checkbox-group-item {
    height: 50px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-flow: row-reverse;
    border-bottom: 1px solid #e6e6e6;
  }

  /deep/.ivu-checkbox {
    float: right;
  }

  .column-title {
    flex: auto;
    margin-left: 16px;
    font-size: 16px;
    color: #000000;
  }
}

/deep/ .column-setting-drawer {
  .t-drawer__content-wrapper {
    min-width: 400px;
    max-width: 800px;
  }
}
</style>
